import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Schedule as PendingIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { volassisAPI } from '../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

function Records() {
  const { currentUser, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [records, setRecords] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(-1);
  const [selectedStatus, setSelectedStatus] = useState('NA');
  const [aggregateData, setAggregateData] = useState(null);

  useEffect(() => {
    if (currentUser) {
      loadRecords();
    }
  }, [currentUser, selectedOrg, selectedStatus]);

  const loadRecords = async () => {
    try {
      setLoading(true);
      
      // Load records
      const recordsResponse = await volassisAPI.getUserRecords(
        currentUser.id,
        selectedOrg,
        selectedStatus
      );
      setRecords(recordsResponse.output || []);

      // Load aggregate data
      const aggregateResponse = await volassisAPI.getUserAggregateData(currentUser.id);
      setAggregateData(aggregateResponse);

      // Load organizations if not already loaded
      if (organizations.length === 0) {
        const orgResponse = await volassisAPI.getAggregateOrganizations(true);
        setOrganizations(orgResponse.output || []);
      }

    } catch (error) {
      console.error('Error loading records:', error);
      setError('Failed to load records');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await volassisAPI.exportRecords(
        currentUser.id,
        selectedOrg,
        selectedStatus
      );
      if (response) {
        toast.success('Records exported successfully!');
      } else {
        toast.error('Failed to export records');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('An error occurred while exporting records');
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'Approved':
        return <Chip icon={<CheckIcon />} label="Approved" color="success" size="small" />;
      case 'Pending':
        return <Chip icon={<PendingIcon />} label="Pending" color="warning" size="small" />;
      case 'Denied':
        return <Chip icon={<CancelIcon />} label="Denied" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const formatDateTime = (dateTimeString) => {
    try {
      return format(new Date(dateTimeString), 'MMM dd, yyyy HH:mm');
    } catch (error) {
      return dateTimeString;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Volunteer Records
      </Typography>
      
      <Grid container spacing={3}>
        {/* Filters */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Organization</InputLabel>
                    <Select
                      value={selectedOrg}
                      label="Organization"
                      onChange={(e) => setSelectedOrg(e.target.value)}
                    >
                      <MenuItem value={-1}>All Organizations</MenuItem>
                      {organizations.map((org) => (
                        <MenuItem key={org.value} value={org.value}>
                          {org.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={selectedStatus}
                      label="Status"
                      onChange={(e) => setSelectedStatus(e.target.value)}
                    >
                      <MenuItem value="NA">All Statuses</MenuItem>
                      <MenuItem value="P">Pending</MenuItem>
                      <MenuItem value="A">Approved</MenuItem>
                      <MenuItem value="D">Denied</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={loadRecords}
                    fullWidth
                  >
                    Refresh
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleExport}
                    fullWidth
                  >
                    Export
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Summary Cards */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">
                {aggregateData?.approved_hours || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Approved Hours
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main">
                {aggregateData?.pending || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Hours
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="error.main">
                {aggregateData?.denied || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Denied Hours
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary.main">
                {aggregateData?.claimed || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Claimed Hours
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Records Table */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Volunteer Records
              </Typography>
              
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {records.length === 0 ? (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                  No records found for the selected criteria.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Organization</TableCell>
                        <TableCell>Start Time</TableCell>
                        <TableCell>End Time</TableCell>
                        <TableCell>Hours</TableCell>
                        <TableCell>Status</TableCell>
                        {isAdmin && <TableCell>Actions</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {records.map((record, index) => (
                        <TableRow key={index}>
                          <TableCell>{record.name || 'N/A'}</TableCell>
                          <TableCell>{formatDateTime(record.start_time)}</TableCell>
                          <TableCell>{formatDateTime(record.end_time)}</TableCell>
                          <TableCell>{record.daily_hours || 0}</TableCell>
                          <TableCell>{getStatusChip(record.status)}</TableCell>
                          {isAdmin && (
                            <TableCell>
                              <Tooltip title="Approve Hours">
                                <IconButton
                                  size="small"
                                  color="success"
                                  disabled={record.status === 'Approved'}
                                >
                                  <CheckIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Records;
